import React from 'react';
import { CTASection } from '../../components/CTASection';
import { FAQSection } from '../../components/FAQSection';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { AlertCircle, Activity, Stethoscope, TrendingUp, BookOpen, FileText } from 'lucide-react';
import { VideoSection, DR_REDDY_VIDEOS } from '../../components/YouTubeEmbed';
import { useSite } from '../../contexts/SiteContext';

export const ShoulderArthritis = () => {
  const { theme } = useSite();
  
  const faqs = [
    {
      question: 'What are the types of shoulder arthritis?',
      answer: 'The main types are: Osteoarthritis (degenerative, most common in patients over 50), Rheumatoid arthritis (autoimmune, affects joint lining), Post-traumatic arthritis (develops after fractures or dislocations), and Rotator cuff tear arthropathy (massive cuff tears leading to secondary arthritis). Each type has different treatment implications.'
    },
    {
      question: 'Can shoulder arthritis be prevented?',
      answer: 'Evidence suggests maintaining good shoulder strength through regular exercise, avoiding repetitive overhead stress, managing weight, and treating injuries promptly can reduce risk. For those with rheumatoid arthritis, disease-modifying medications help prevent joint damage.'
    },
    {
      question: 'When is shoulder replacement needed?',
      answer: 'According to AAOS guidelines, shoulder replacement is indicated when: conservative treatments fail after 3-6 months, pain significantly limits daily activities, X-rays show severe joint damage (bone-on-bone), and quality of life is substantially affected. The decision is always individualized.'
    },
    {
      question: 'How successful is shoulder replacement surgery?',
      answer: 'Registry data shows shoulder replacement has 90-95% success rate for pain relief. The Australian Joint Registry reports 95% implant survival at 10 years. Most patients achieve significant functional improvement and return to activities like golf, swimming, and daily tasks.'
    },
    {
      question: 'What is the difference between total and reverse shoulder replacement?',
      answer: 'Anatomic total shoulder replacement (TSA) is used when the rotator cuff is intact - it replicates normal anatomy. Reverse shoulder replacement (RSA) is designed for patients with severe arthritis AND rotator cuff tears - it reverses the ball-and-socket, allowing the deltoid muscle to compensate for absent cuff function.'
    },
    {
      question: 'How long does a shoulder replacement last?',
      answer: 'Modern shoulder implants have excellent longevity. Published data shows 90-95% of implants function well at 15-20 years. Younger, more active patients may require revision surgery eventually, but implant technology continues to improve.'
    }
  ];

  return (
    <div className="min-h-screen">
      <section className="bg-gradient-to-br from-orange-50 to-white py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Shoulder Arthritis Treatment in Hyderabad
            </h1>
            <p className="text-xl text-gray-600">
              Joint replacement options by Dr. Deepthi Nandan Reddy at Apollo Hospitals. 95% success rate.
            </p>
          </div>
        </div>
      </section>

      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              What is Shoulder Arthritis?
            </h2>
            <div className="prose prose-lg max-w-none text-gray-700 leading-relaxed space-y-4">
              <p>
                Shoulder arthritis is a degenerative condition where the smooth cartilage covering the shoulder joint bones wears away, leading to pain, stiffness, and loss of function. As the protective cartilage deteriorates, the bones may rub against each other, causing inflammation and pain.
              </p>
              <p>
                The shoulder has two joints that can be affected: the glenohumeral joint (main shoulder joint between the upper arm bone and shoulder blade) and the acromioclavicular (AC) joint (where the collarbone meets the shoulder blade).
              </p>
              <p>
                While shoulder arthritis is less common than hip or knee arthritis, it can significantly impact daily activities and quality of life, making simple tasks like reaching overhead or behind your back difficult and painful.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-8">
              Causes and Risk Factors
            </h2>
            <div className="grid md:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <AlertCircle className="w-5 h-5 text-medical-orange" />
                    Primary Causes
                  </CardTitle>
                </CardHeader>
                <CardContent className="text-gray-600 space-y-2">
                  <p>• Age-related wear and tear (osteoarthritis)</p>
                  <p>• Rheumatoid arthritis or other inflammatory conditions</p>
                  <p>• Previous shoulder injury or fracture</p>
                  <p>• Rotator cuff tear arthropathy</p>
                  <p>• Avascular necrosis (loss of blood supply to bone)</p>
                </CardContent>
              </Card>
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <AlertCircle className="w-5 h-5 text-medical-orange" />
                    Risk Factors
                  </CardTitle>
                </CardHeader>
                <CardContent className="text-gray-600 space-y-2">
                  <p>• Age over 50 years</p>
                  <p>• Family history of arthritis</p>
                  <p>• Previous shoulder injury or surgery</p>
                  <p>• Repetitive overhead activities</p>
                  <p>• Obesity increasing joint stress</p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center gap-3 mb-6">
              <Activity className="w-8 h-8 text-medical-orange" />
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900">
                Symptoms
              </h2>
            </div>
            <div className="bg-orange-50 p-6 rounded-lg space-y-3">
              <p className="text-gray-700">• Progressive shoulder pain that worsens with activity</p>
              <p className="text-gray-700">• Pain at night interfering with sleep</p>
              <p className="text-gray-700">• Limited range of motion</p>
              <p className="text-gray-700">• Grinding, clicking, or catching sensations</p>
              <p className="text-gray-700">• Weakness in the shoulder</p>
              <p className="text-gray-700">• Stiffness making daily tasks difficult</p>
              <p className="text-gray-700">• Swelling around the joint</p>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center gap-3 mb-6">
              <Stethoscope className="w-8 h-8 text-medical-orange" />
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900">
                Diagnosis
              </h2>
            </div>
            <div className="prose prose-lg max-w-none text-gray-700 leading-relaxed space-y-4">
              <p>
                <strong>Medical History:</strong> Discussion of symptoms, pain patterns, previous injuries, and impact on daily activities.
              </p>
              <p>
                <strong>Physical Examination:</strong> Assessment of range of motion, strength, tenderness, and grinding sensations.
              </p>
              <p>
                <strong>X-rays:</strong> Show joint space narrowing, bone spurs, and changes in bone structure characteristic of arthritis.
              </p>
              <p>
                <strong>MRI or CT Scan:</strong> Provide detailed images of cartilage damage, bone quality, and rotator cuff status for surgical planning.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center gap-3 mb-6">
              <TrendingUp className="w-8 h-8 text-medical-orange" />
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900">
                Treatment Options
              </h2>
            </div>
            <div className="space-y-6">
              <Card className="border-l-4 border-l-medical-orange">
                <CardHeader>
                  <CardTitle>Conservative Treatment</CardTitle>
                </CardHeader>
                <CardContent className="text-gray-700 space-y-3">
                  <p>• Rest and activity modification to reduce joint stress</p>
                  <p>• NSAIDs or acetaminophen for pain management</p>
                  <p>• Physiotherapy to maintain strength and mobility</p>
                  <p>• Ice and heat therapy</p>
                  <p>• Corticosteroid injections for temporary relief</p>
                  <p>• Hyaluronic acid injections (viscosupplementation)</p>
                  <p>• Lifestyle modifications including weight management</p>
                </CardContent>
              </Card>
              <Card className="border-l-4 border-l-medical-orange">
                <CardHeader>
                  <CardTitle>Surgical Treatment</CardTitle>
                </CardHeader>
                <CardContent className="text-gray-700 space-y-4">
                  <p><strong>When Surgery is Needed:</strong> Conservative treatments no longer provide adequate relief, significant functional limitations, or severe joint damage.</p>
                  <div className="bg-orange-50 p-4 rounded-lg">
                    <p className="font-semibold mb-2">Surgical Options:</p>
                    <p>• <strong>Arthroscopy:</strong> For mild arthritis, removing debris and bone spurs</p>
                    <p>• <strong>Total Shoulder Replacement:</strong> Replacing damaged surfaces with artificial components</p>
                    <p>• <strong>Reverse Shoulder Replacement:</strong> For arthritis with rotator cuff tears</p>
                    <p>• <strong>Hemiarthroplasty:</strong> Replacing only the humeral head in younger patients</p>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              Recovery Expectations
            </h2>
            <div className="prose prose-lg max-w-none text-gray-700 leading-relaxed space-y-4">
              <p>
                <strong>Conservative Treatment:</strong> Ongoing management with periods of improvement and flare-ups. Lifestyle modifications and regular physiotherapy can maintain function for years.
              </p>
              <p>
                <strong>Post-Surgical Recovery:</strong> Hospital stay of 1-2 days. Sling use for 4-6 weeks. Physiotherapy begins within days of surgery. Most daily activities resume at 3 months. Full recovery takes 6-12 months.
              </p>
              <p>
                <strong>Outcomes:</strong> Over 90% of patients experience significant pain relief and improved function after shoulder replacement. Most can return to low-impact activities and enjoy better quality of life.
              </p>
            </div>
          </div>
        </div>
      </section>

      <FAQSection faqs={faqs} />
      <CTASection />
    </div>
  );
};
